import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import { debounce } from 'lodash'
import { fromZonedTime } from 'date-fns-tz'
import { endOfDay } from 'date-fns'
import { useTranslation } from 'react-i18next'

import SearchField from '../../FormLegacy/FormFieldsV2/SearchField'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import PaginationFooter from '../../PaginationFooter/PaginationFooter'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import API from '../../../api/api'
import FeaturedTable from '../../FeaturedTable/FeaturedTable'
import VendorDetailsModal from '../modals/VisitorDetailsModal/VendorDetailsModal'
import GuestDetailsModal from '../modals/VisitorDetailsModal/GuestDetailsModal'
import ModalsContainer from '../modals/ModalsContainer'

import { getHistoryColumns } from './helpers'

const History = ({ selectedBuilding }) => {
  const { t } = useTranslation(['visitors'])
  const { analyticsOpen, newVisitorModalOpen, setNewVisitorModalOpen } = useOutletContext()
  const [visitorHistory, setVisitorHistory] = useState([])
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [isHistoryLoading, setIsHistoryLoading] = useState(false)
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const { displaySnackbar } = useSnackbar()
  const [visitorDetails, setVisitorDetails] = useState(null)

  const onDetailsClick = (historyItem) => {
    setVisitorDetails({ id: historyItem.visitorId, type: historyItem.type })
  }

  const fetchVisitorHistory = async (
    searchTerm = search,
    start = startDate,
    end = endDate,
    page = pageNumber,
    perPage = showOnPage,
  ) => {
    if (!selectedBuilding) {
      return
    }
    setIsHistoryLoading(true)
    const skip = (page - 1) * perPage
    try {
      const response = await API.getVisitors({
        buildingId: selectedBuilding.id || '1',
        search: searchTerm,
        skip,
        limit: perPage,
        start: start ? fromZonedTime(start, 'UTC').getTime() : undefined,
        end: end ? fromZonedTime(endOfDay(end), 'UTC').getTime() : undefined,
      })

      setVisitorHistory(response.data.history || [])
      setPaginationItemsCount(response.data.count || 0)
    } catch {
      displaySnackbar(t('visitors:snackbar.errorFetchingHistory'), true)
    } finally {
      setIsHistoryLoading(false)
    }
  }

  const debouncedFetchHistory = debounce((searchTerm, start, end, page, perPage) => {
    fetchVisitorHistory(searchTerm, start, end, page, perPage)
  }, 500)

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value
    setSearch(searchTerm)
    setPageNumber(1)
    debouncedFetchHistory(searchTerm, startDate, endDate, 1, showOnPage)
  }

  const handleDateChange = ([start, end]) => {
    setStartDate(start)
    setEndDate(end)
    setPageNumber(1)
    fetchVisitorHistory(search, start, end, 1, showOnPage)
  }

  useEffect(() => {
    fetchVisitorHistory(search, startDate, endDate, pageNumber, showOnPage)
  }, [pageNumber, showOnPage, selectedBuilding, startDate, endDate])

  const columns = getHistoryColumns(
    t,
    onDetailsClick,
    selectedBuilding ? selectedBuilding.timezone : 'America/Chicago',
  )

  const maxTableHeight = {
    maxHeight: analyticsOpen ? `calc(100vh - 550px)` : `calc(100vh - 400px)`,
  }

  return (
    <div className="visitors">
      <div className="visitors__navbar visitors__navbar--history">
        <div className="visitors__navbar-filters">
          <DatePicker
            className="input date-range-picker"
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            maxDate={new Date()}
            placeholderText={t('visitors:toolbar.selectDateRange')}
            isClearable={true}
            onChange={handleDateChange}
          />
          <SearchField
            placeholder={t('visitors:toolbar.searchByNameOrEmail')}
            value={search}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      <div
        className={`ctp-visitors__table ${isHistoryLoading ? 'is-loading' : ''}`}
        style={maxTableHeight}
      >
        {visitorHistory.length > 0 ? (
          <FeaturedTable columns={columns} data={visitorHistory} />
        ) : (
          <div className="no-results">{t('visitors:views.noVisitorHistory')}</div>
        )}
      </div>
      {visitorHistory.length > 0 && (
        <PaginationFooter
          showOnPage={showOnPage}
          pageNumber={pageNumber}
          paginationItemsCount={paginationItemsCount}
          setSiteLoading={setIsHistoryLoading}
          setShowOnPage={setShowOnPage}
          setPageNumber={setPageNumber}
        />
      )}
      {visitorDetails && visitorDetails.type === 'vendor' && (
        <VendorDetailsModal vendorId={visitorDetails.id} onClose={() => setVisitorDetails(null)} />
      )}
      {visitorDetails && visitorDetails.type === 'guest' && (
        <GuestDetailsModal guestId={visitorDetails.id} onClose={() => setVisitorDetails(null)} />
      )}
      <ModalsContainer
        newVisitorModalOpen={newVisitorModalOpen}
        setNewVisitorModalOpen={setNewVisitorModalOpen}
      />
    </div>
  )
}

History.propTypes = {
  selectedBuilding: PropTypes.object,
}

export default connect((state) => {
  return {
    selectedBuilding: selectActiveBuilding(state),
  }
})(History)
